import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import {
    KeyboardArrowLeft as BackIcon,
    KeyboardArrowRight as NextIcon,
    Minimize as MinimizeIcon,
    RocketLaunch as RocketIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDeliveryCockpitTourContext } from '../../contexts/DeliveryCockpitTourContext';
import { getTourPersonaLabel } from '../../constants/deliveryCockpitTourPersonas';
import TourProgressBar from './TourProgressBar';

/**
 * Non-modal guided tour coach panel with route-gated progression.
 */
const DeliveryCockpitTourCoach = () => {
    const navigate = useNavigate();
    const {
        isTourOpen,
        persona,
        currentStep,
        activeStepIndex,
        stepCount,
        canGoNext,
        isLastStep,
        isMinimized,
        setIsMinimized,
        routeMatched,
        dismissTour,
        goNext,
        goBack,
        markStepDone,
        skipStep,
    } = useDeliveryCockpitTourContext();

    const [dontShowOnNextLogin, setDontShowOnNextLogin] = useState(true);

    const handleCta = useCallback(() => {
        if (!currentStep?.cta?.path) {
            return;
        }
        navigate(currentStep.cta.path);
    }, [currentStep, navigate]);

    const handleSkipOrClose = useCallback(() => {
        dismissTour(dontShowOnNextLogin);
    }, [dismissTour, dontShowOnNextLogin]);

    useEffect(() => {
        if (!isTourOpen || isMinimized) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                setIsMinimized(true);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isTourOpen, isMinimized, setIsMinimized]);

    if (!isTourOpen || isMinimized) {
        if (isTourOpen && isMinimized) {
            return (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 16,
                        right: 16,
                        zIndex: (theme) => theme.zIndex.modal + 1,
                        pointerEvents: 'auto',
                    }}
                >
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => setIsMinimized(false)}
                        data-testid="delivery-cockpit-tour-restore"
                    >
                        Resume tour
                    </Button>
                </Box>
            );
        }
        return null;
    }

    const personaLabel = getTourPersonaLabel(persona);
    const completionMode = currentStep?.completion || 'manual';
    const showMarkDone = completionMode === 'route' || completionMode === 'manual';
    const showSkipStep = completionMode === 'optional' || completionMode === 'route';

    return (
        <Dialog
            open={isTourOpen}
            onClose={() => {}}
            hideBackdrop
            disableEscapeKeyDown
            disableScrollLock
            disableEnforceFocus
            disableAutoFocus
            disableRestoreFocus
            maxWidth="sm"
            fullWidth
            aria-labelledby="delivery-cockpit-tour-title"
            data-testid="delivery-cockpit-tour-coach"
            sx={{
                pointerEvents: 'none',
                zIndex: (theme) => theme.zIndex.modal + 2,
                '& .MuiDialog-container': {
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end',
                },
            }}
            PaperProps={{
                sx: {
                    pointerEvents: 'auto',
                    m: 2,
                    maxWidth: 440,
                    width: '100%',
                    boxShadow: 8,
                },
            }}
        >
            <DialogTitle
                id="delivery-cockpit-tour-title"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}
            >
                <RocketIcon color="primary" aria-hidden />
                <Box sx={{ flex: 1 }}>
                    {currentStep?.title}
                    <Chip
                        label={personaLabel}
                        size="small"
                        sx={{ ml: 1, verticalAlign: 'middle' }}
                    />
                </Box>
                <IconButton
                    aria-label="Minimize tour"
                    onClick={() => setIsMinimized(true)}
                    size="small"
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                    data-testid="delivery-cockpit-tour-minimize"
                >
                    <MinimizeIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <TourProgressBar
                        activeStep={activeStepIndex}
                        stepCount={stepCount}
                        stepTitle={currentStep?.title}
                    />

                    <Typography variant="body1" color="text.secondary">
                        {currentStep?.body}
                    </Typography>

                    {currentStep?.roleNote ? (
                        <Typography variant="caption" color="text.secondary">
                            {currentStep.roleNote}
                        </Typography>
                    ) : null}

                    {routeMatched && currentStep?.route ? (
                        <Typography variant="caption" color="success.main">
                            You are on the right screen — click Next to continue.
                        </Typography>
                    ) : null}

                    {currentStep?.cta ? (
                        <Box>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleCta}
                                data-testid="delivery-cockpit-tour-cta"
                            >
                                {currentStep.cta.label}
                            </Button>
                        </Box>
                    ) : null}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 2, py: 1.5, gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {showMarkDone ? (
                        <Button
                            size="small"
                            variant="text"
                            onClick={markStepDone}
                            disabled={routeMatched}
                            data-testid="delivery-cockpit-tour-mark-done"
                        >
                            I did this
                        </Button>
                    ) : null}
                    {showSkipStep ? (
                        <Button
                            size="small"
                            variant="text"
                            color="inherit"
                            onClick={skipStep}
                            data-testid="delivery-cockpit-tour-skip-step"
                        >
                            Skip this step
                        </Button>
                    ) : null}
                </Box>

                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={dontShowOnNextLogin}
                            onChange={(event) => setDontShowOnNextLogin(event.target.checked)}
                            size="small"
                            inputProps={{ 'data-testid': 'delivery-cockpit-tour-dont-show-again' }}
                        />
                    )}
                    label="Don't show this tour again on next login"
                    sx={{
                        m: 0,
                        alignItems: 'flex-start',
                        '& .MuiFormControlLabel-label': { typography: 'body2' },
                    }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button
                        onClick={handleSkipOrClose}
                        color="inherit"
                        data-testid="delivery-cockpit-tour-dismiss"
                    >
                        {isLastStep ? 'Close tour' : 'Skip tour'}
                    </Button>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            disabled={activeStepIndex === 0}
                            onClick={goBack}
                            startIcon={<BackIcon />}
                            data-testid="delivery-cockpit-tour-back"
                        >
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            onClick={goNext}
                            endIcon={isLastStep ? null : <NextIcon />}
                            disabled={isLastStep || !canGoNext}
                            data-testid="delivery-cockpit-tour-next"
                        >
                            Next
                        </Button>
                    </Box>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default DeliveryCockpitTourCoach;
